/**
 * Shared form-validation visibility hook for the create / modify campaign flows.
 *
 * Owns the cross-flow concerns: per-field `touched` state, the `submitted`
 * latch, and the "which errors are visible right now" computation. Each
 * consuming hook (useCreateCampaign / useModifyCampaign) keeps only its
 * flow-specific submit orchestration (API call, response handling) and
 * delegates validation visibility here.
 *
 * Visibility rules:
 * - On load: no errors shown.
 * - Once a field is touched: its error becomes visible and updates live.
 * - On submit: all errors become visible regardless of touched state.
 */

import { useState, useCallback, useMemo } from 'react';
import { CampaignType } from 'src/@types/valueMatching/enums';
import { CampaignConfigurationData } from 'src/@types/valueMatching/campaignConfiguration';
import { UseCampaignFormResult } from 'src/hooks/useCampaignForm/useCampaignForm';
import {
    getFieldErrors,
    FieldErrors,
    FieldKey,
} from 'src/utils/valueMatching/form/formValidationUtils';
import { SHARE_FIELDS } from 'src/constants';

const DEFAULT_PROPAGATION_DELAY_MINUTES = 0;

export interface UseCampaignFormValidationParams {
    form: UseCampaignFormResult;
    config: CampaignConfigurationData | null;
    campaignType: CampaignType | undefined;
    formType: 'create' | 'modify';
}

export interface UseCampaignFormValidationResult {
    /** Per-field error map filtered by the visibility rules above. */
    fieldErrors: FieldErrors;
    /** Marks a field as touched so its error is shown from now on. */
    markTouched: (field: FieldKey) => void;
    /** `true` once `markSubmitted` has been called at least once. */
    submitted: boolean;
    /** Flips the submitted latch so ALL field errors become visible. */
    markSubmitted: () => void;
}

export function useCampaignFormValidation({
    form,
    config,
    campaignType,
    formType,
}: UseCampaignFormValidationParams): UseCampaignFormValidationResult {
    const [touched, setTouched] = useState<Set<FieldKey>>(new Set());
    const [submitted, setSubmitted] = useState(false);

    const markTouched = useCallback((field: FieldKey) => {
        setTouched((prev) => {
            if (prev.has(field)) return prev;
            const next = new Set(prev);
            next.add(field);
            return next;
        });
    }, []);

    const markSubmitted = useCallback(() => setSubmitted(true), []);

    const allErrors: FieldErrors = useMemo(() => {
        if (!campaignType) return {};
        const propagationDelay = config?.propagationDelayInMinutes ?? DEFAULT_PROPAGATION_DELAY_MINUTES;
        return getFieldErrors(form, campaignType, formType, propagationDelay);
    }, [form, campaignType, config, formType]);

    /**
     * Cross-field rules (fundingSharesSum, brandName) surface when any related
     * field in the group is touched, even if the specific error-key field
     * isn't. E.g., "shares must sum to 100" appears as soon as any share field
     * is touched.
     */
    const fieldErrors: FieldErrors = useMemo(() => {
        if (submitted) return allErrors;
        const visible: FieldErrors = {};
        const anyShareFieldTouched = SHARE_FIELDS.some((field) => touched.has(field));
        (Object.keys(allErrors) as FieldKey[]).forEach((key) => {
            if (touched.has(key)) visible[key] = allErrors[key];
            else if (key === 'fundingSharesSum' && anyShareFieldTouched) visible[key] = allErrors[key];
            else if (key === 'brandName' && touched.has('brandShare')) visible[key] = allErrors[key];
        });
        return visible;
    }, [allErrors, touched, submitted]);

    return { fieldErrors, markTouched, submitted, markSubmitted };
}
